import React from 'react';
import { useChatApiVersion } from '../hooks/useChatApiVersion';
import type { ChatApiVersion } from '../services/chatApiVersion';

export interface ChatApiVersionToggleProps {
    // The rollout's canSwitch; where it is false, ChatApiVersionGuard holds everyone on the default.
    visible: boolean;
    /**
     * Applies the change. The caller owns it because switching version has to
     * drop cached chat data — ids mean different things on either side — and
     * only the app knows its own query client.
     */
    onChange: (version: ChatApiVersion) => void;
    className?: string;
}

/**
 * The rollback, as a button.
 *
 * Deliberately a visible control rather than a hidden setting: the point is
 * that anyone who hits a problem with v2 can be back on v1 in one click,
 * without a deploy, without an env var, and without asking anyone.
 */
export function ChatApiVersionToggle({
    visible,
    onChange,
    className = ''
}: ChatApiVersionToggleProps): React.ReactElement | null {
    const { version } = useChatApiVersion();

    if (!visible) {
        return null;
    }

    // One button, sized like the chat input's other toggles, showing the version
    // in use. v2 is marked in ODO teal rather than the dark "on" fill those
    // toggles use, so it never reads as another activity-style switch. The v1
    // state keeps a transparent border so switching never shifts the layout.
    const onV2 = version === 'v2';
    const next: ChatApiVersion = onV2 ? 'v1' : 'v2';
    const label = onV2
        ? 'Chat reads api/v2 (database). Switch back to v1'
        : 'Chat reads api/v1 (Monday). Switch to v2';

    return (
        <button
            type="button"
            onClick={() => onChange(next)}
            aria-pressed={onV2}
            aria-label={label}
            title={label}
            className={`w-7 h-7 flex items-center justify-center rounded border font-mono text-[10px] font-semibold transition-colors ${
                onV2
                    ? 'border-[#007E87]/40 bg-[#007E87]/10 text-[#007E87] hover:bg-[#007E87]/15'
                    : 'border-transparent text-gray-500 hover:bg-gray-200 hover:text-gray-700'
            } ${className}`}
        >
            {version}
        </button>
    );
}
